'use client';

import { useState, useCallback, useRef } from 'react';
export type SharePointAuthErrorType = 'expired' | 'no_access' | 'no_refresh_token' | 'popup_blocked' | 'unknown';
export interface SharePointAuthError {
  type: SharePointAuthErrorType;
  message: string;
  action: string;
}
export function useSharePointAuth() {
  const [sharePointToken, setSharePointToken] = useState<string | null>(null);
  const [isAcquiring, setIsAcquiring] = useState(false);
  const [authError, setAuthError] = useState<SharePointAuthError | null>(null);
  const expiresAtRef = useRef<number>(0);
  const acquireSharePointToken = useCallback(async (): Promise<string | null> => {
    // Return cached token if still valid (with 60s buffer)
    if (sharePointToken && Date.now() < expiresAtRef.current - 60_000) {
      return sharePointToken;
    }
    setIsAcquiring(true);
    setAuthError(null);
    try {
      const res = await fetch('/api/auth/sharepoint-token', {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) {
        const code = data.error as string | undefined;
        const description = (data.description ?? data.error ?? 'Could not acquire a SharePoint token.') as string;
        setAuthError({
          type: code === 'no_refresh_token' ? 'no_refresh_token' : code === 'unauthenticated' ? 'no_access' : 'no_access',
          message: description,
          action: code === 'no_refresh_token' ? 'Sign out and sign back in' : 'Try again'
        });
        setSharePointToken(null);
        return null;
      }
      setSharePointToken(data.access_token);
      expiresAtRef.current = Date.now() + (data.expires_in ?? 3600) * 1000;
      return data.access_token;
    } catch (err) {
      setAuthError({
        type: 'unknown',
        message: err instanceof Error ? err.message : String(err),
        action: 'Try again'
      });
      setSharePointToken(null);
      return null;
    } finally {
      setIsAcquiring(false);
    }
  }, [sharePointToken]);
  return {
    sharePointToken,
    acquireSharePointToken,
    isAcquiring,
    authError
  };
}